import React from 'react';
import { UUID } from '@umanforge/common';
import { CalendarEvent } from 'interfaces/calendar.interface.js';
import { ErrorIcons } from 'components/other/_ErrorIcons';
import { useTranslation } from 'react-i18next';
import Status from 'interfaces/status.class';
import { dayjs } from '@umanforge/common';
import { EditPenSVG } from 'images/editPen.svg';
import { useChronogramEvent } from '../hook/chronogramEvent.hook';

interface ChronogramEventProps {
  taskId: UUID;
  event: CalendarEvent
  columnRange: { start: number, end: number }
  celSize: number
  draggable?: boolean
  outlined?: boolean;
  lineId: string;
  date: dayjs.Dayjs
  className?: string;
  stackable?: boolean;
  selectedTab: 'distribution' | 'location' | 'task' | 'equipment' | string;
  isAdmin?: boolean;

  openModal: ( event: CalendarEvent ) => void
  onDragEnd: ( ) => void;
  onDragStart: ( event: CalendarEvent ) => void;
  onOver?: ( id: string | undefined ) => void;
}

export const ChronogramEvent: React.FC<ChronogramEventProps> = ( {
  event,
  columnRange,
  openModal,
  celSize,
  draggable = true,
  lineId,
  date,

  onDragEnd,
  onDragStart,
  className = '',
  onOver = () => { /* */},
  stackable = true,
  isAdmin = false
} ) => {
  const { t } = useTranslation();

  const {
    colStart, colEnd, startoffset, maxWidth, width, errors,
    isPastEvent,
    dragged,
    handleDragStart,
    handleDragEnd,
    overflowHour
  } = useChronogramEvent(
    event,
    columnRange,
    celSize,
    lineId,
    date,
    onDragEnd,
    onDragStart,
    stackable
  );

  return (
    <div
      className={`max-h-6 px-0.5 my-px ${ !event.temporary ? 'z-10' : 'opacity-70' } relative group ${className}`}
      style={{ gridColumnStart: colStart, gridColumnEnd: colEnd }}
    >
      <div
        className={'h-full'}
        style={{ transform: 'translateX(' + ( startoffset * 0.2 ) + 'rem)', width: `calc(${ width }% )` }}
        onClick={() => { openModal( event ); }}
      >
        <div
          className={`w-full h-full -z-10 ${ ( !isPastEvent && draggable || !isAdmin ) ? 'dragable-element cursor-pointer' : 'cursor-not-allowed'} `}
          draggable={ !isPastEvent && draggable }

          onDragStart={ e => {
            e.dataTransfer.setDragImage( new Image(), 0, 0 );
            if ( isPastEvent ) {return;}
            handleDragStart( event );
          }}

          onDragEnd={ () => {
            if ( isPastEvent ) {return;}
            handleDragEnd( );
          }}

          onMouseOver={ () => {
            onOver( event.id );
          } }
          onMouseLeave={ () => {
            onOver( undefined );
          } }
        >
          <div
            id="event_task"
            className={'relative h-6 grid rounded-sm prevent-select'}
            style={{ backgroundColor: Status.statusToColor( event.task?.status as string ) }}
          >
            <div className={'overflow-hidden w-full h-full rounded-sm' + ( ( event.understaffed ) && ' understaffed ' ) + ( ( stackable && event.stacked && lineId != 'not_assigned' && lineId != 'not_assigned' ) && ' stacked-task' )}>
              <div className={'relative h-full' + ( overflowHour ? 'pl-2' : '' )} style={{ maxWidth: maxWidth + 'rem' }}>
                { overflowHour &&
                  <div className="absolute -ml-2" title={ t( 'translation:errors.hour_out_of_bound' )}>
                    <svg fill="url(#grad1)" className="w-2.5 h-full" viewBox="0 0 10 32" version="1.1" xmlns="http://www.w3.org/2000/svg">
                      <g id="fade">
                        <path d="M0 0 l5 0 l5 4 l-5 4  l5 4 l-5 4  l5 4 l-5 4  l5 4 l-5 4 l-5 0"></path>
                        <defs>
                          <linearGradient id="grad1" x1="0%" x2="100%" y1="0%" y2="0%">
                            <stop offset="0%" stopColor="#fff" />
                            <stop offset="70%" stopColor="#fff8" />
                          </linearGradient>
                        </defs>
                      </g>
                    </svg>
                  </div>
                }
                <span className='text-xs sm:whitespace-nowrap truncate flex items-center w-fit h-full gap-x-1 ml-1' >
                  { errors.map( ( err, i ) => {
                    return <ErrorIcons error={err} key={`${i}_${err}`} size={5} className='-mt-0.5 mr-2'/>;
                  } ) }
                  { event.title }
                </span>
              </div>
            </div>
            { dragged &&
              <div className='absolute w-5 h-5 rounded bg-green-500 outline outline-2 outline-white self-start place-self-end -mt-[.35rem] -mr-2'>
                <EditPenSVG width={16} stroke="#fff" strokeWidth={1} className='m-0.5' />
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
};